import { useState, useRef } from "react";
import { toast } from "sonner";
import { Mic, Square, Play, Pause, Trash2, Loader2, Volume2, Sparkles, Navigation, Save, Image as ImageIcon } from "lucide-react";
import { uploadStopAudio, clearStopAudio, audioSrc, setStopDirections, uploadStopPhoto } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const StopRecorder = ({ routeId, stop, onChange }) => {
  const [audio, setAudio] = useState(stop.audio || "");
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [showScript, setShowScript] = useState(false);
  const [dir, setDir] = useState(stop.directions || "");
  const [savingDir, setSavingDir] = useState(false);
  const [photoUp, setPhotoUp] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const audioElRef = useRef(null);
  const timerRef = useRef(null);

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const words = (stop.story || "").trim().split(/\s+/).filter(Boolean).length;
  const suggestedSec = Math.max(15, Math.round(words / 2.5));

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setUploading(true);
        try {
          const res = await uploadStopAudio(routeId, stop.id, blob);
          setAudio(res.audio);
          toast.success(`Saved your voice for "${stop.name}"`);
          onChange && onChange();
        } catch {
          toast.error("Could not save recording");
        } finally {
          setUploading(false);
        }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch {
      toast.error("Microphone access was blocked. Allow mic access and try again.");
    }
  };

  const stopRec = () => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      setRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const togglePlay = () => {
    if (!audio) return;
    if (!audioElRef.current) audioElRef.current = new Audio(audioSrc(audio));
    if (playing) {
      audioElRef.current.pause();
      audioElRef.current.currentTime = 0;
      setPlaying(false);
    } else {
      audioElRef.current.src = audioSrc(audio);
      audioElRef.current.onended = () => setPlaying(false);
      audioElRef.current.play();
      setPlaying(true);
    }
  };

  const clear = async () => {
    try {
      await clearStopAudio(routeId, stop.id);
      setAudio("");
      toast.success("Recording removed — this stop will use the AI voice");
      onChange && onChange();
    } catch {
      toast.error("Could not remove recording");
    }
  };

  return (
    <div className="border border-white/10 rounded-lg p-3" data-testid={`vo-stop-${stop.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium truncate">{stop.name}</p>
          {audio ? (
            <p className="text-xs text-emerald-300 flex items-center gap-1"><Volume2 className="w-3 h-3" /> Your recording</p>
          ) : (
            <p className="text-xs text-[#F5F5F0]/40 flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI voice (fallback)</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {audio && (
            <button data-testid={`vo-play-${stop.id}`} onClick={togglePlay} className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white">
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
          )}
          {uploading ? (
            <span className="p-2 text-[#F5F5F0]/60"><Loader2 className="w-4 h-4 animate-spin" /></span>
          ) : recording ? (
            <Button data-testid={`vo-stop-btn-${stop.id}`} onClick={stopRec} size="sm" className="rounded-full bg-red-600 hover:bg-red-700 animate-pulse">
              <Square className="w-3 h-3 mr-1" /> Stop
            </Button>
          ) : (
            <Button data-testid={`vo-record-${stop.id}`} onClick={startRec} size="sm" className="rounded-full bg-[#001A4D] hover:bg-[#00287A]">
              <Mic className="w-3 h-3 mr-1" /> {audio ? "Re-record" : "Record"}
            </Button>
          )}
          {audio && !recording && (
            <button data-testid={`vo-clear-${stop.id}`} onClick={clear} className="p-2 text-[#F5F5F0]/50 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
          )}
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 text-xs">
        <button data-testid={`vo-script-toggle-${stop.id}`} onClick={() => setShowScript((v) => !v)} className="text-[#F5F5F0]/60 hover:text-white underline underline-offset-2">
          {showScript ? "Hide script" : "Suggested script"}
        </button>
        <span className="text-[#F5F5F0]/25">·</span>
        {recording ? (
          <span data-testid={`vo-timer-${stop.id}`} className={`font-mono ${elapsed > 120 ? "text-red-400" : "text-amber-200"}`}>● {fmt(elapsed)}{elapsed > 120 ? " — wrap up" : ""}</span>
        ) : (
          <span className="text-[#F5F5F0]/40 font-mono">aim ~{fmt(suggestedSec)} · keep under 2 min</span>
        )}
      </div>
      {showScript && (
        <p data-testid={`vo-script-${stop.id}`} className="mt-2 text-sm text-[#F5F5F0]/75 leading-relaxed bg-white/5 rounded-lg p-3 max-h-40 overflow-y-auto">{stop.story}</p>
      )}
      <div className="mt-3">
        <p className="overline text-[#F5F5F0]/40 mb-1 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Photos {stop.images?.length ? `(${stop.images.length})` : ""}</p>
        <input data-testid={`vo-photo-${stop.id}`} type="file" accept="image/*" disabled={photoUp}
          onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; setPhotoUp(true); try { await uploadStopPhoto(routeId, stop.id, f); toast.success("Photo added"); onChange && onChange(); } catch { toast.error("Could not upload photo"); } finally { setPhotoUp(false); e.target.value = ""; } }}
          className="text-xs text-white/70 file:mr-2 file:rounded-full file:border-0 file:bg-white/10 file:text-white file:px-3 file:py-1" />
        {photoUp && <Loader2 className="w-3 h-3 animate-spin inline ml-2" />}
      </div>
      <div className="mt-3">
        <p className="overline text-[#F5F5F0]/40 mb-1 flex items-center gap-1"><Navigation className="w-3 h-3" /> Directions to next stop (optional)</p>
        <Textarea data-testid={`vo-directions-${stop.id}`} value={dir} onChange={(e) => setDir(e.target.value)} placeholder="e.g. Head south on the beachfront path, then follow the shared cycleway past the surf club." className="bg-white/5 border-white/10 text-sm" />
        <Button
          data-testid={`vo-directions-save-${stop.id}`}
          size="sm"
          disabled={savingDir}
          onClick={async () => { setSavingDir(true); try { await setStopDirections(routeId, stop.id, dir); toast.success("Directions saved"); onChange && onChange(); } catch { toast.error("Could not save directions"); } finally { setSavingDir(false); } }}
          className="mt-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
        >
          <Save className="w-3 h-3 mr-1" /> Save directions
        </Button>
      </div>
    </div>
  );
};

export const VoiceoverManager = ({ route, onChange }) => {
  if (!route) return null;
  return (
    <div className="space-y-3" data-testid="voiceover-manager">
      <p className="text-xs text-[#F5F5F0]/40">Record your own voice for each stop. Stops without a recording use the AI voice automatically.</p>
      {route.stops.map((s) => (
        <StopRecorder key={s.id} routeId={route.id} stop={s} onChange={onChange} />
      ))}
    </div>
  );
};
